import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Select, Space, message,
} from 'antd';
import csrfHeaders from '../../../utils/csrfHeaders';
import { captureAnalytics } from '../../../actions/preload';

// NextGen document type mappings, edited inline in the Manage Categories table.
// One mapping per integration: the server saves it to every synced practice and
// reports any practice it couldn't apply to. A category may stay unmapped (it
// just can't auto-upload). The server's revision covers the enabled categories
// and their labels, so this state lives above the table: the cells and the bar
// share it, and it reloads whenever the page's categories change.

const SYNC_MESSAGE = 'NextGen document metadata hasn’t been synced. Contact Medsender to sync your metadata.';
const failureMessage = (error) => error.response?.data?.error || 'Could not load NextGen mappings. Please try again.';
const plural = (count, one, many) => (count === 1 ? one : many);
const lagReason = (practice) => (practice.missing_types?.length
  ? `${practice.name} (missing ${practice.missing_types.join(', ')})`
  : practice.name);

export const NextgenMappingContext = createContext({ hasNextgen: false });

export const NextgenMappingProvider = ({ isAdmin, categoriesKey, children }) => {
  const [index, setIndex] = useState({ destinations: [], hasNextgen: false, error: '' });
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  // Unsaved selections only; saved mappings come from `data`.
  const [dirty, setDirty] = useState({});
  const [suggested, setSuggested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  // Loads and saves share one counter: only the newest response may update state.
  const request = useRef(0);

  useEffect(() => {
    mounted.current = true;
    axios.get('/api/v1/nextgen_category_mappings').then(({ data: body }) => {
      if (!mounted.current) return;
      const destinations = body.destinations || [];
      setIndex({ destinations, hasNextgen: Boolean(body.has_nextgen), error: '' });
      const initial = destinations.find((entry) => entry.synced) || destinations[0];
      if (initial) setSelected(initial.credential_id);
    }).catch((err) => {
      if (mounted.current) setIndex((previous) => ({ ...previous, error: failureMessage(err) }));
    });
    return () => { mounted.current = false; };
  }, []);

  const base = selected && `/api/v1/nextgen_category_mappings/${selected}`;

  const load = useCallback(async () => {
    if (!base) return;
    // A newer load (another integration, or changed categories) wins.
    request.current += 1;
    const token = request.current;
    setBusy(true);
    setError('');
    try {
      const response = await axios.get(base);
      if (!mounted.current || token !== request.current) return;
      setData(response.data);
    } catch (err) {
      if (mounted.current && token === request.current) setError(failureMessage(err));
    } finally {
      if (mounted.current && token === request.current) setBusy(false);
    }
  }, [base]);

  // A category change only refreshes the revision and keeps unsaved selections.
  useEffect(() => {
    if (selected) load();
  }, [selected, categoriesKey, load]);

  const value = useMemo(() => {
    const types = data?.document_types || [];
    const categories = data?.categories || [];
    // Any available type is a valid mapping; `automatic` says whether uploads can file it unattended.
    const ready = (id) => types.some((type) => type.id === id && type.available);
    // Savable: at least one category mapped, and every chosen type still available.
    const valid = (map) => {
      const chosen = categories.flatMap((category) => (map[category.id] ? [map[category.id]] : []));
      return chosen.length > 0 && chosen.every(ready);
    };
    // Before setup the server's prefills are a starting point to review. After
    // setup, an unmapped category stays visibly empty until someone picks a type.
    const draft = data ? { ...(data.active ? {} : data.suggested_mappings), ...data.mappings, ...dirty } : {};
    // Synced practices not on the saved mapping (a skipped save, or newly synced).
    const lagging = data?.active ? (data.practices || []).filter((practice) => practice.synced && !practice.matches) : [];

    const save = async (map, mode) => {
      request.current += 1;
      const token = request.current;
      setBusy(true);
      captureAnalytics('nextgen_category_mappings_save_requested', { mode });
      try {
        const mapped = categories.filter((category) => map[category.id]);
        const mappings = Object.fromEntries(mapped.map((category) => [category.id, map[category.id]]));
        const response = await axios.put(base, { revision: data.revision, mappings }, { headers: csrfHeaders() });
        if (!mounted.current) return;
        setDirty({});
        setSuggested(false);
        // A load started meanwhile (categories changed) may have read the old
        // state; reload so the page shows what was just saved.
        if (token === request.current) setData(response.data); else load();
        const skipped = response.data.skipped || [];
        if (skipped.length) {
          message.warning(`Saved, but not for ${skipped.map((practice) => practice.name).join(', ')}. See the note above the table.`);
        } else {
          message.success(mode === 'inline' ? 'NextGen document type saved.' : 'NextGen document type mappings saved.');
        }
      } catch (err) {
        if (!mounted.current) return;
        message.error(failureMessage(err));
        // The usual cause is a stale revision: refresh it and keep the selection.
        load();
      } finally {
        if (mounted.current && token === request.current) setBusy(false);
      }
    };

    const suggest = async () => {
      // Suggestions belong to this integration and revision: a reload or an
      // integration switch while Auto-map runs makes them stale, so drop them.
      request.current += 1;
      const token = request.current;
      setBusy(true);
      setError('');
      captureAnalytics('nextgen_category_auto_map_requested');
      try {
        const response = await axios.post(`${base}/suggest`, {
          revision: data.revision,
          selected_category_ids: Object.keys(draft).filter((id) => draft[id]),
        }, { headers: csrfHeaders() });
        if (!mounted.current || token !== request.current) return;
        const { suggestions } = response.data;
        setDirty((previous) => ({ ...suggestions, ...previous }));
        setSuggested(Object.keys(suggestions).length > 0);
        if (!Object.keys(suggestions).length) {
          message.info('Auto-map found no confident matches. Select the remaining document types manually.');
        }
      } catch (err) {
        if (mounted.current && token === request.current) setError(failureMessage(err));
      } finally {
        if (mounted.current && token === request.current) setBusy(false);
      }
    };

    return {
      hasNextgen: index.hasNextgen,
      indexError: index.error,
      destinations: index.destinations,
      credentialIds: [...new Set(index.destinations.map((entry) => entry.credential_id))],
      selected,
      // Switching integration discards the other one's draft.
      selectCredential: (id) => {
        captureAnalytics('nextgen_category_mapping_integration_selected');
        setSelected(id);
        setData(null);
        setDirty({});
        setSuggested(false);
      },
      isAdmin,
      data,
      draft,
      dirty,
      busy,
      error,
      suggested,
      ready,
      canSave: valid(draft),
      lagging,
      mappedCount: categories.filter((category) => ready(draft[category.id])).length,
      requiresFieldsCount: categories.filter((category) => types.some((type) => (
        type.id === draft[category.id] && type.available && !type.automatic
      ))).length,
      select: (categoryId, id) => {
        captureAnalytics('nextgen_category_mapping_selected', { document_category_id: categoryId });
        setDirty((previous) => ({ ...previous, [categoryId]: id }));
        const next = { ...draft, [categoryId]: id };
        // Once set up, a change applies at once; before that it's a draft to review.
        // Other unsaved selections (Auto-map suggestions, a failed save) wait for
        // Save mappings, so nothing is applied that the admin hasn't reviewed.
        const othersPending = Object.keys(dirty).some((key) => key !== String(categoryId));
        if (data.active && !othersPending && valid(next)) save(next, 'inline');
      },
      suggest,
      saveDraft: () => save(draft, 'explicit'),
      discard: () => {
        captureAnalytics('nextgen_category_mappings_discarded');
        setDirty({});
        setSuggested(false);
      },
      retry: () => { captureAnalytics('nextgen_category_mappings_retried'); load(); },
    };
  }, [index, selected, isAdmin, data, dirty, busy, error, suggested, base, load]);

  return <NextgenMappingContext.Provider value={value}>{children}</NextgenMappingContext.Provider>;
};

NextgenMappingProvider.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  categoriesKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// Type IDs mean nothing to users; show one only to tell same-named types apart.
const typeLabel = (type, types) => {
  const shared = types.some((other) => other.id !== type.id && other.name === type.name);
  return shared ? `${type.name} (${type.id.slice(-6)})` : type.name;
};

const UNAVAILABLE = 'This document type is unavailable. Select another type or contact Medsender to sync metadata.';
// Types Medsender can fill on its own say nothing extra. The rest name the fields
// a person has to supply in Send to Chart; automatic uploads won't file them.
const extraFields = (type) => (type.available && !type.automatic ? type.required_fields || [] : []);
const requiresLabel = (type) => `Requires: ${extraFields(type).join(', ')}`;

const RequiredFields = ({ type }) => (extraFields(type).length === 0 ? null : (
  <div className="manage-categories__nextgen-status manage-categories__nextgen-status--manual">
    {requiresLabel(type)}
  </div>
));

RequiredFields.propTypes = { type: PropTypes.object.isRequired };

export const NextgenTypeCell = ({ categoryId, enabled, name }) => {
  const {
    data, draft, isAdmin, busy, ready, select,
  } = useContext(NextgenMappingContext);
  if (!enabled || !data?.metadata_loaded || !categoryId) return <span className="manage-categories__row-meta">—</span>;

  const selectedId = draft[categoryId];
  const type = data.document_types.find((entry) => entry.id === selectedId);
  // An unmapped category is fine: it just can't auto-upload, so it stays quiet.
  const problem = selectedId && !ready(selectedId) ? UNAVAILABLE : null;
  const problemLine = problem && <div role="alert" className="manage-categories__nextgen-problem">{problem}</div>;
  const status = !problem && type && <RequiredFields type={type} />;

  if (!isAdmin) {
    return (
      <div>
        {type
          ? <span className="manage-categories__nextgen-type">{type.name}</span>
          : <span className="manage-categories__row-meta">Not mapped</span>}
        {problemLine}
        {status}
      </div>
    );
  }
  return (
    <div className="manage-categories__nextgen-cell">
      <Select
        aria-label={`NextGen document type for ${name}`}
        data-testid={`nextgen-type-${categoryId}`}
        showSearch
        size="small"
        optionFilterProp="label"
        // The list names required fields; once chosen, the line below repeats them.
        optionLabelProp="name"
        className={`manage-categories__nextgen-select${problem ? ' manage-categories__nextgen-select--problem' : ''}`}
        disabled={busy}
        allowClear
        value={selectedId || undefined}
        placeholder="Select a document type"
        options={data.document_types.map((entry) => ({
          value: entry.id,
          name: typeLabel(entry, data.document_types),
          label: `${typeLabel(entry, data.document_types)}${extraFields(entry).length ? ` · ${requiresLabel(entry)}` : ''}`,
          disabled: !entry.available,
        }))}
        onChange={(id) => select(categoryId, id)}
      />
      {problemLine}
      {status}
    </div>
  );
};

NextgenTypeCell.propTypes = {
  categoryId: PropTypes.number,
  enabled: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
};

NextgenTypeCell.defaultProps = { categoryId: null };

export const NextgenMappingBar = () => {
  const {
    hasNextgen, indexError, destinations, credentialIds, selected, selectCredential, isAdmin, data, dirty, busy,
    error, suggested, canSave, lagging, mappedCount, requiresFieldsCount, suggest, saveDraft, discard, retry,
  } = useContext(NextgenMappingContext);

  if (indexError) return <Alert className="manage-categories__nextgen-alert" type="error" showIcon message={indexError} />;
  if (!hasNextgen) return null;
  if (!destinations.length) return <Alert className="manage-categories__nextgen-alert" type="warning" showIcon message={SYNC_MESSAGE} />;

  const categoryCount = data?.categories.length || 0;
  const missing = categoryCount - mappedCount;
  const hasDraft = Object.keys(dirty).length > 0;
  const editable = isAdmin && data?.metadata_loaded;
  const prefilled = Boolean(data && !data.active && Object.keys(data.suggested_mappings || {}).length);
  const practices = data?.practices || [];
  const syncedCount = practices.filter((practice) => practice.synced).length;
  const unsyncedCount = practices.length - syncedCount;

  return (
    <div className="manage-categories__nextgen-bar">
      <div className="manage-categories__nextgen-bar-row">
        <Space wrap size={12}>
          <span className="manage-categories__nextgen-bar-title">NextGen document types</span>
          {credentialIds.length > 1 && (
            <Select
              aria-label="NextGen integration"
              data-testid="nextgen-integration"
              size="small"
              className="manage-categories__nextgen-practice"
              value={selected || undefined}
              disabled={busy}
              options={credentialIds.map((id) => ({ value: id, label: `Integration ${id}` }))}
              onChange={selectCredential}
            />
          )}
          {data?.metadata_loaded && (
            <span className="manage-categories__count-chip">
              <span className="dot" />
              {`${mappedCount} of ${categoryCount} enabled mapped`}
            </span>
          )}
          {requiresFieldsCount > 0 && (
            <span className="manage-categories__count-chip manage-categories__count-chip--manual">
              <span className="dot" />
              {`${requiresFieldsCount} ${plural(requiresFieldsCount, 'requires', 'require')} extra fields`}
            </span>
          )}
          {syncedCount > 1 && (
            <span className="manage-categories__row-meta">
              {`Applies to all ${syncedCount} synced practices${unsyncedCount ? ` · ${unsyncedCount} not synced` : ''}`}
            </span>
          )}
        </Space>
        {editable && (
          <Space wrap>
            {missing > 0 && <Button size="small" disabled={busy} onClick={suggest}>Auto-map</Button>}
            {data.active && hasDraft && <Button size="small" disabled={busy} onClick={discard}>Discard changes</Button>}
            {(!data.active || hasDraft || lagging.length > 0) && (
              <Button size="small" type="primary" disabled={busy || !canSave} onClick={saveDraft}>Save mappings</Button>
            )}
          </Space>
        )}
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button size="small" onClick={retry}>Retry</Button>}
        />
      )}
      {data && !data.metadata_loaded && <Alert type="warning" showIcon message={SYNC_MESSAGE} />}
      {data?.metadata_loaded && !data.active && (
        <Alert
          type="info"
          showIcon
          message="Choose NextGen document types for your categories, then save to start using them. Categories you leave empty can still be filed with Send to Chart, just not auto-uploaded. Existing filing settings stay in use until you save."
        />
      )}
      {lagging.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Not used yet at ${lagging.map(lagReason).join(', ')}. Automatic uploads there keep their current filing settings. ${isAdmin ? 'Save mappings to apply' : 'An organization admin can save the mappings to apply them'}, or contact Medsender if a type is missing.`}
        />
      )}
      {(suggested || prefilled) && <Alert type="info" showIcon message="Review the suggested selections before saving. Suggestions have not been applied to uploads." />}
    </div>
  );
};
